import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ---------------------------------------------------------------------------
// Types — mirror server/child_support_aggregator.py
// ---------------------------------------------------------------------------

/**
 * A category is only $0.00 once someone says so. `not_reviewed` is excluded from
 * every total rather than counted as zero — the distinction that keeps a
 * worksheet from asserting a self-employed obligor has no self-employment income.
 */
export type ResourceStatus = 'established' | 'confirmed_none' | 'not_reviewed';

export interface ResourceLine {
  code: string;
  label: string;
  statute: string;
  amount: number | null;
  status: ResourceStatus;
  counts: boolean;
  source: string;
  income_profile_id: number | null;
  statement_ids: number[];
  transaction_ids: number[];
  is_candidate: boolean;
  override_reason: string | null;
  note: string | null;
  /** 'Ex. B' / 'Ex. B, C' / 'Ex. B–E'. Empty when no document backs the line. */
  exhibit_citation: string;
}

export interface NetResources {
  gross_monthly_resources: number;
  social_security_taxes: number;
  federal_income_tax: number;
  state_income_tax: number;
  union_dues: number;
  child_coverage_deductions: number;
  nondiscretionary_retirement: number;
  total_deductions: number;
  net_resources: number;
  tax_year: number;
}

export interface PartyWorksheet {
  party: 'client' | 'opposing';
  role: 'obligor' | 'obligee' | 'unassigned';
  display_name: string;
  lines: ResourceLine[];
  net_resources: NetResources | null;
  unknown_line_codes: string[];
  evidence_only_from_other_side: boolean;
  candidates: IncomeStream[];
  income_comparison: IncomeComparison | null;
  comparison_is_material: boolean;
}

export interface GuidelineResult {
  net_resources: number;
  applied_net_resources: number;
  cap: number;
  cap_citation: string;
  over_cap: boolean;
  num_children: number;
  percentage: number;
  percentage_display: string;
  presumptive_amount: number;
  low_income_schedule: boolean;
  six_plus_floor: boolean;
  method: string;
  notes: string[];
}

export interface MedicalSupport {
  health_premium_additional_support: number;
  dental_premium_additional_support: number;
  cash_medical_support: number;
  health_reasonable_cost_ceiling: number;
  dental_reasonable_cost_ceiling: number;
  cash_medical_ceiling: number;
  notes: string[];
}

export interface AboveCap {
  presumptive_amount: number;
  proven_needs: number;
  unmet_needs: number;
  obligor_share_of_unmet: number;
  maximum_orderable: number;
  notes: string[];
}

export interface StepDownStage {
  effective_after: string;
  children_remaining: number;
  percentage: number;
  monthly_amount: number;
}

export interface Findings154130 {
  required: boolean;
  required_reason: string;
  obligor_net_resources: number | null;
  obligee_net_resources: number | null;
  percentage_applied: string | null;
  guideline_amount: number | null;
  ordered_amount: number | null;
  variance_reason: string | null;
  guidelines_unjust_or_inappropriate: boolean;
}

/** A recurring deposit stream found in the bank data. Never counted until accepted. */
export interface IncomeStream {
  key: string;
  label: string;
  category: string;
  category_label: string;
  line_code: string;
  statute: string;
  party: string | null;
  occurrences: number;
  months_observed: number;
  total_amount: number;
  monthly_average: number;
  average_deposit: number;
  first_date: string | null;
  last_date: string | null;
  transaction_ids: number[];
  statement_ids: number[];
  confidence: 'high' | 'medium' | 'low';
}

export interface IncomeComparison {
  documented_monthly: number | null;
  deposits_monthly: number;
  delta: number;
  delta_pct: number | null;
  stream_count: number;
}

export interface Exhibit {
  letter: string;
  kind: 'statement' | 'income_document';
  label: string;
  filename: string | null;
  statement_id: number | null;
  account: string | null;
  period: string | null;
  pages: number[];
}

/** One input the computation rests on, with its state and (when missing) a remedy. */
export interface Requirement {
  key: string;
  label: string;
  status: 'established' | 'candidate' | 'incomplete' | 'missing';
  detail: string;
  statute: string;
  party: string | null;
  required: boolean;
  request_from: 'client' | 'opposing' | 'both' | null;
  request_text: string | null;
}

/** Ready-to-send request language for one recipient, honoring cheapest-source. */
export interface DocumentRequest {
  recipient: 'client' | 'opposing';
  recipient_label: string;
  item_keys: string[];
  lines: string[];
  text: string;
}

export interface ReadinessItem {
  code: string;
  message: string;
  line_codes?: string[];
}

export interface ChildRow {
  id: number | null;
  initials: string | null;
  full_name: string | null;
  date_of_birth: string | null;
  is_before_court: boolean;
  is_disabled_adult: boolean;
  notes?: string | null;
}

export interface ChildSupportWorksheet {
  as_of: string;
  tax_year: number;
  params_version: string;
  order_vessel: string;
  suit_type: 'divorce' | 'sapcr';
  cap: number;
  obligor_party: 'client' | 'opposing' | null;
  obligee_party: 'client' | 'opposing' | null;
  parties: Record<string, PartyWorksheet>;
  children: ChildRow[];
  children_before_court: number;
  children_outside: number;
  guideline: GuidelineResult | null;
  alternative_154_128: GuidelineResult | null;
  alternative_154_129: GuidelineResult | null;
  selected_method: string | null;
  selected: GuidelineResult | null;
  medical_support: MedicalSupport;
  above_cap: AboveCap | null;
  base_support: number;
  total_monthly_obligation: number;
  step_down: StepDownStage[];
  variance: { amount: number | null; factors: string[]; reason: string | null };
  findings_154_130: Findings154130;
  coverage_notes: string[];
  exhibit_index: Exhibit[];
  unattributed_streams: IncomeStream[];
  requirements: Requirement[];
  document_requests: DocumentRequest[];
  readiness: { blocking: ReadinessItem[]; warnings: ReadinessItem[]; ready: boolean };
}

export interface ChildSupportProfile {
  obligor_party?: string | null;
  obligor_children_outside?: number | null;
  health_carried_by?: string | null;
  health_premium_total?: number | null;
  health_plan_dependents?: number | null;
  health_coverage_unavailable?: boolean | null;
  cash_medical_support?: number | null;
  dental_carried_by?: string | null;
  dental_premium_total?: number | null;
  dental_plan_dependents?: number | null;
  health_insurer?: string | null;
  health_policy_number?: string | null;
  dental_insurer?: string | null;
  dental_policy_number?: string | null;
  uninsured_split_obligor_pct?: number | null;
  obligor_union_dues?: number | null;
  obligor_nondiscretionary_retirement?: number | null;
  obligor_pays_social_security?: boolean | null;
  obligee_union_dues?: number | null;
  obligee_nondiscretionary_retirement?: number | null;
  obligee_pays_social_security?: boolean | null;
  resource_overrides?: Record<string, Record<string, { amount: number; reason?: string }>> | null;
  confirmed_none_lines?: Record<string, string[]> | null;
  suit_type?: string | null;
  proven_needs_monthly?: number | null;
  obligor_allocation_share?: number | null;
  multiple_household_method?: string | null;
  variance_factors?: string[] | null;
  variance_amount?: number | null;
  variance_reason?: string | null;
  tax_year?: number | null;
  order_vessel?: string | null;
  first_payment_date?: string | null;
}

export interface ChildSupportRunMeta {
  id: number;
  title: string;
  generated_at: string | null;
  tax_year: number | null;
  params_version: string | null;
  cap_amount: number | null;
  total_monthly_obligation: number | null;
  documents: { kind: string; filename: string; size: number }[];
}

export interface ChildSupportParams {
  version: string;
  net_resources_cap: { effective_from: string; effective_to: string | null; amount: number; citation: string }[];
  low_income_threshold: number;
  guideline_percentages: Record<string, number>;
  low_income_percentages: Record<string, number>;
  latest_published_tax_year: number;
  tax_years: Record<string, { label: string }>;
  minimum_wage_monthly_gross: number;
  cash_medical_support_max_pct: number;
  dental_reasonable_cost_max_pct: number;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useChildSupportParams() {
  return useQuery<ChildSupportParams>({
    queryKey: ['child-support-params'],
    queryFn: async () => {
      const { data } = await api.get('/child-support/params');
      return data;
    },
    staleTime: Infinity, // checked-in legal constants; they do not change mid-session
  });
}

export function useChildSupportWorksheet(caseId: string | undefined) {
  return useQuery<ChildSupportWorksheet>({
    queryKey: ['child-support-worksheet', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/child-support/worksheet`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useChildSupportProfile(caseId: string | undefined) {
  return useQuery<ChildSupportProfile>({
    queryKey: ['child-support-profile', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/child-support/profile`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useChildren(caseId: string | undefined) {
  return useQuery<ChildRow[]>({
    queryKey: ['children', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/children`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useChildSupportRuns(caseId: string | undefined) {
  return useQuery<ChildSupportRunMeta[]>({
    queryKey: ['child-support-runs', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/child-support/runs`);
      return data;
    },
    enabled: !!caseId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Anything that changes the number has to refresh the worksheet with it. */
function invalidateWorksheet(queryClient: ReturnType<typeof useQueryClient>, caseId: string) {
  queryClient.invalidateQueries({ queryKey: ['child-support-worksheet', caseId] });
}

export function useSaveChildSupportProfile(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ChildSupportProfile) => {
      const { data } = await api.put(`/cases/${caseId}/child-support/profile`, patch);
      return data as ChildSupportProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-support-profile', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useAddChild(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (child: Partial<ChildRow>) => {
      const { data } = await api.post(`/cases/${caseId}/children`, child);
      return data as ChildRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useUpdateChild(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<ChildRow> & { id: number }) => {
      const { data } = await api.patch(`/cases/${caseId}/children/${id}`, patch);
      return data as ChildRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useDeleteChild(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (childId: number) => {
      const { data } = await api.delete(`/cases/${caseId}/children/${childId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useAcceptIncomeStream(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      party: string;
      stream_key: string;
      line_code: string;
      monthly_amount: number;
      reason?: string;
      transaction_ids?: number[];
      statement_ids?: number[];
    }) => {
      const { data } = await api.post(`/cases/${caseId}/child-support/accept-stream`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-support-profile', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useDismissIncomeStream(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { party: string; stream_key: string; reason?: string }) => {
      const { data } = await api.post(`/cases/${caseId}/child-support/dismiss-stream`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-support-profile', caseId] });
      invalidateWorksheet(queryClient, caseId);
    },
  });
}

export function useGenerateChildSupportPacket(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/child-support/packet`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-support-runs', caseId] });
    },
  });
}

export function useDeleteChildSupportRun(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (runId: number) => {
      const { data } = await api.delete(`/cases/${caseId}/child-support/runs/${runId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-support-runs', caseId] });
    },
  });
}
